import type { RuleCheck } from '../hooks/useRuleCheck';
import { t } from '../i18n';
import { placeText, problemText, type CheckedProblem } from '../ruleCheck';

/**
 * How the engine's rule check (`useRuleCheck`) reads on screen: one quiet status line, the
 * "Check rules" button, and a list of problems. Shared by the release dialog and the rule
 * editor so the states read the same everywhere.
 */

/** "Checking…", "Couldn't check the rules — the server did not answer", "Demo mode does not check rules", … */
export function CheckStatusLine({ check, testid = 'rule-check-status' }: { check: RuleCheck; testid?: string }) {
  switch (check.status) {
    case 'off':
      return null;
    case 'demo':
      return (
        <div className="small muted" data-testid={testid} data-state="demo">
          {t('problem.check.demo')}
        </div>
      );
    case 'pending':
    case 'checking':
      return (
        <div className="small muted" data-testid={testid} data-state="checking" role="status">
          {t('problem.check.running')}
        </div>
      );
    case 'failed':
      return (
        <div className="small check-failed" data-testid={testid} data-state="failed" role="alert">
          {t('problem.check.failed')}
        </div>
      );
    case 'done': {
      const n = check.result?.problems.length ?? 0;
      return (
        <div className="small muted" data-testid={testid} data-state={n === 0 ? 'ok' : 'problems'} role="status">
          {n === 0 ? t('problem.check.ok') : t(n === 1 ? 'problem.check.found.one' : 'problem.check.found.many', { n })}
        </div>
      );
    }
  }
}

/** "Check rules": ask the engine now instead of waiting for the pause after an edit. */
export function CheckButton({ check, testid = 'check-rules' }: { check: RuleCheck; testid?: string }) {
  if (check.status === 'off' || check.status === 'demo') return null;
  return (
    <button type="button" className="btn" data-testid={testid} disabled={check.status === 'checking'} onClick={check.checkNow}>
      {check.status === 'checking' ? t('problem.check.running') : t('problem.check.button')}
    </button>
  );
}

/**
 * One line per problem: where (the engine's document path, in words — `placeText`) and what,
 * in the active language. `pathOf` lets a caller show a shorter path (the rule editor's are
 * relative to the rule); a path it maps to '' is left out.
 */
export function ProblemList({
  problems,
  limit,
  pathOf = (p) => p.path,
  className = 'small',
}: {
  problems: readonly CheckedProblem[];
  limit?: number;
  pathOf?: (p: CheckedProblem) => string;
  className?: string;
}) {
  const shown = limit === undefined ? problems : problems.slice(0, limit);
  return (
    <ul>
      {shown.map((p, i) => {
        const where = placeText(pathOf(p), p.params);
        return (
          <li key={i} className={className}>
            {where && (
              <>
                <span className="problem-place">{where}</span> —{' '}
              </>
            )}
            {problemText(p)}
          </li>
        );
      })}
    </ul>
  );
}
